import { z } from "zod";
import { CategoryInput, DepartmentInput, HttpsUrl, LIST_ITEM, ProgramInput, ProgramStateInput } from "./directory.js";
import { IncomeTableRowInput, RuleInputBase, requiredHasNoNote, indicativeOnlyIncome } from "./matching.js";
import { TemplateInput } from "./worksheets.js";
import { IntentRuleInput, ProblemInput, SynonymInput } from "./kb.js";
const SourceRef = z.object({
    url: HttpsUrl, title: z.string().max(300).optional(), publisher: z.string().max(200).optional(),
    retrievedAt: z.string().datetime(), fields: z.array(z.string()).min(1).default(["*"]),
});
const VerificationRef = z.object({
    status: z.enum(["verified", "pending"]), pendingFields: z.array(z.string()).default([]),
    verifiedAt: z.string().datetime().optional(), note: z.string().max(2000).optional(),
});
const Lists = z.object({
    contacts: z.array(LIST_ITEM.contacts).optional(), documents: z.array(LIST_ITEM.documents).optional(),
    steps: z.array(LIST_ITEM.steps).optional(), faqs: z.array(LIST_ITEM.faqs).optional(),
    denial_reasons: z.array(LIST_ITEM.denial_reasons).optional(),
}).strict().default({});
export const CategoriesFile = z.object({
    kind: z.literal("categories"),
    categories: z.array(CategoryInput.omit({ parentId: true }).extend({ parent: z.string().optional() })),
});
export const DepartmentFile = z.object({
    kind: z.literal("department"),
    department: DepartmentInput.omit({ parentId: true }).extend({ parent: z.string().optional() }),
    lists: z.object({ contacts: z.array(LIST_ITEM.contacts).optional(), faqs: z.array(LIST_ITEM.faqs).optional() }).strict().default({}),
    sources: z.array(SourceRef).default([]),
    verification: VerificationRef.default({ status: "pending", pendingFields: [] }),
});
const StateBlock = z.object({
    fields: ProgramStateInput.omit({ departmentId: true }).default({}),
    department: z.string().optional(),
    lists: Lists,
    sources: z.array(SourceRef).default([]),
    verification: VerificationRef.default({ status: "pending", pendingFields: [] }),
});
export const ProgramFile = z.object({
    kind: z.literal("program"),
    program: ProgramInput.omit({ categoryId: true, departmentId: true }).extend({ category: z.string(), department: z.string().optional() }),
    lists: Lists,
    sources: z.array(SourceRef).default([]),
    verification: VerificationRef.default({ status: "pending", pendingFields: [] }),
    states: z.record(z.string().length(2), StateBlock).default({}),
});
const Line = z.string().trim().min(1).max(300);
export const ProblemFile = z.object({
    kind: z.literal("problem"),
    // sort and active are the portal's own (admins reorder and hide problems there); an import never sets them.
    problem: ProblemInput.omit({ categoryId: true, questionsToAsk: true, infoNeeded: true, active: true, sort: true }).extend({
        category: z.string().optional(),
        questionsToAsk: z.array(Line).max(30).default([]),
        infoNeeded: z.array(Line).max(30).default([]),
    }),
    /** Linked programs, most useful first. Leave the key out to keep the links already in the portal. */
    programs: z.array(z.object({ program: z.string(), reason: z.string().trim().max(300).optional() })).max(50).optional(),
    faqs: z.array(LIST_ITEM.faqs).optional(),
    sources: z.array(SourceRef).default([]),
    verification: VerificationRef.default({ status: "pending", pendingFields: [] }),
});
export const SynonymsFile = z.object({ kind: z.literal("synonyms"), synonyms: z.array(SynonymInput) });
export const IntentRulesFile = z.object({
    kind: z.literal("intent-rules"),
    rules: z.array(IntentRuleInput.omit({ problemId: true }).extend({ problem: z.string() })),
});
export const EligibilityRulesFile = z.object({
    kind: z.literal("eligibility-rules"),
    program: z.string().min(1),
    rules: z.array(RuleInputBase.pick({ field: true, operator: true, value: true, kind: true }).extend({
        state: z.string().length(2).optional(), unit: z.string().max(40).optional(), sourceUrl: HttpsUrl,
        note: z.string().trim().max(400).optional(),
    }).superRefine((r, ctx) => {
        requiredHasNoNote({ kind: r.kind, note: r.note ?? null }, ctx);
        indicativeOnlyIncome(r, ctx);
        if (r.kind === "indicative" && !r.note)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["note"], message: "An indicative rule needs a note" });
    })),
    sources: z.array(SourceRef).default([]),
    verification: VerificationRef.optional(),
});
export const IncomeTablesFile = z.object({
    kind: z.literal("income-tables"),
    tables: z.array(IncomeTableRowInput.pick({ kind: true, year: true, householdSize: true, amount: true, period: true }).extend({
        state: z.string().length(2).optional(), sourceUrl: HttpsUrl.optional(),
    })),
    sources: z.array(SourceRef).default([]),
});
/** A worksheet template. Imports only ever create or update drafts; publishing stays an admin action. */
export const TemplateFile = z.object({
    kind: z.literal("template"),
    template: z.object({
        programSlug: z.string().min(1), stateCode: z.string().length(2).optional(),
        title: z.string(), instructions: z.string(), fields: z.array(z.unknown()), sourceUrl: HttpsUrl,
    }),
    sources: z.array(SourceRef).default([]),
}).superRefine((f, ctx) => {
    const t = f.template;
    const u = new URL(t.sourceUrl);
    if (u.username || u.password)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["template", "sourceUrl"], message: "sourceUrl must not contain credentials" });
    if (!f.sources.some((s) => s.url === t.sourceUrl))
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["template", "sourceUrl"], message: "sourceUrl must be listed in sources" });
    const r = TemplateInput.safeParse({ ...t, stateCode: t.stateCode ?? null });
    if (!r.success)
        for (const i of r.error.issues)
            ctx.addIssue({ ...i, path: ["template", ...i.path] });
});
//# sourceMappingURL=content.js.map